import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { isAxiosError } from 'axios';
import { Info } from 'lucide-react';
import type { AddLenderUserRequest, Lender, LenderUser } from '../../api/lenders';
import { checkUserEmailUnique } from '../../api/lenderOnboarding';
import { useAddLenderUser } from '../../hooks/useLenders';
import { useProducts } from '../../hooks/useProducts';
import ProductAccordionTree from './wizard/components/ProductAccordionTree';
import WizardDialog from './wizard/components/WizardDialog';
import { buildProductTree } from './wizard/components/productTree';
import { buildUserSchema, type UserFormValues } from './wizard/schemas/user.schema';
import { USER_ROLE_LABELS } from './wizard/types';
import {
  DISCARD_ACCESS_BODY,
  DISCARD_ACCESS_CANCEL,
  DISCARD_ACCESS_CONFIRM,
  DISCARD_ACCESS_TITLE,
  EMAIL_DUPLICATE,
  ACCESS_HINT,
  ACCESS_ON_TEXT,
  EDIT_ACCESS_LABEL,
} from './wizard/copy';
import closeIcon from './assets/product-close.svg';
import toggleOff from './assets/product-toggle-off.svg';
import toggleOn from './assets/product-toggle-on.svg';

const ADD_FAILED = 'Unable to add the user. Please try again.';

// The MeridianLink user names show when the lender has that integration, but are not required.
const schema = buildUserSchema({ requireMlConsumerUserName: false });

const filled = (value: string) => value.trim() || undefined;

interface AddLenderUserDrawerProps {
  lender: Lender;
  onClose: () => void;
  onAdded: (user: LenderUser) => void;
}

export default function AddLenderUserDrawer({ lender, onClose, onAdded }: AddLenderUserDrawerProps) {
  const addUser = useAddLenderUser();
  const { data: catalog } = useProducts();
  const tree = useMemo(() => buildProductTree(catalog ?? []), [catalog]);
  const lenderProducts = useMemo(
    () => (lender.products ?? []).map((product) => (typeof product === 'string' ? product : product.urn)),
    [lender.products],
  );
  const hasMlConsumer = Boolean(lender.integrations?.meridianlink);
  const hasMlMortgage = Boolean(lender.integrations?.['meridianlink-mortgage']);

  // null = the user inherits every product of the lender; a record restricts them to the urns set
  // true, so {} means access to nothing.
  const [productAccess, setProductAccess] = useState<Record<string, boolean> | null>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const [checkedEmail, setCheckedEmail] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const { register, watch, handleSubmit, formState: { errors, isValid } } = useForm<UserFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: 'lender-standard',
      mlConsumerUserName: '',
      mlMortgageUserName: '',
    },
  });
  const email = watch('email');

  useEffect(() => {
    let cancelled = false;
    if (!email || errors.email) {
      setDuplicate(false);
      setCheckedEmail(null);
      return;
    }
    checkUserEmailUnique(email, lender.name)
      .then((unique) => { if (!cancelled) { setDuplicate(!unique); setCheckedEmail(email); } })
      .catch(() => { if (!cancelled) { setDuplicate(false); setCheckedEmail(email); } });
    return () => { cancelled = true; };
  }, [email, errors.email, lender.name]);

  // Holds Add User from the moment the email is well formed until its duplicate check lands.
  const awaitingCheck = !!email && !errors.email && checkedEmail !== email;
  const canSave = isValid && !duplicate && !awaitingCheck && !addUser.isPending;

  const toggleAccess = () => {
    // Off is always confirmed: it reverts the user to inheriting everything.
    if (productAccess !== null) {
      setConfirmingDiscard(true);
    } else {
      setProductAccess(Object.fromEntries(lenderProducts.map((urn) => [urn, true])));
    }
  };

  const toggleProduct = (urn: string) => {
    setProductAccess((current) => (current === null ? current : { ...current, [urn]: !current[urn] }));
  };

  const submit = handleSubmit(async (values) => {
    setFailed(false);
    const data: AddLenderUserRequest = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email,
      role: values.role,
      productAccess,
    };
    const mlConsumerUserName = hasMlConsumer ? filled(values.mlConsumerUserName) : undefined;
    const mlMortgageUserName = hasMlMortgage ? filled(values.mlMortgageUserName) : undefined;
    if (mlConsumerUserName) data.mlConsumerUserName = mlConsumerUserName;
    if (mlMortgageUserName) data.mlMortgageUserName = mlMortgageUserName;
    try {
      const created = await addUser.mutateAsync({ urn: lender.urn, data });
      onAdded(created);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        setDuplicate(true);
      } else {
        setFailed(true);
      }
    }
  });

  const accessOn = productAccess !== null;

  return (
    <div className="fixed inset-0 z-50" role="presentation">
      <div
        className="absolute inset-x-0 bottom-0 top-[60px] bg-black/40"
        onClick={addUser.isPending ? undefined : onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-lender-user-title"
        className="absolute bottom-0 right-0 top-[60px] w-full max-w-[668px] border border-[#ddd] bg-surface shadow-lg dark:border-border-subtle"
      >
        <button
          type="button"
          aria-label="Close add user"
          onClick={onClose}
          disabled={addUser.isPending}
          className="absolute right-6 top-[22px] z-10"
        >
          <img src={closeIcon} alt="" />
        </button>
        <form onSubmit={submit} className="flex h-full min-h-0 flex-col px-6 pb-6 pt-[22px] sm:px-10 sm:pb-14">
          {/* Outside the scrolling area, so its scrollbar starts below the close button. */}
          <h2 id="add-lender-user-title" className="shrink-0 text-[21px] font-medium leading-8 text-content">
            Add User
          </h2>
          <div className="mt-5 min-h-0 flex-1 space-y-5 overflow-y-auto">
            <section className="space-y-3">
              <h3 className="text-[16px] font-medium leading-6 text-content">User Info</h3>
              <div className="grid grid-cols-1 gap-x-2 gap-y-3 sm:grid-cols-2">
                <div className="field">
                  <label className="label" htmlFor="add-user-first-name">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input id="add-user-first-name" className="input" {...register('firstName')} />
                  {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName.message}</p>}
                </div>
                <div className="field">
                  <label className="label" htmlFor="add-user-last-name">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input id="add-user-last-name" className="input" {...register('lastName')} />
                  {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName.message}</p>}
                </div>
                <div className="field">
                  <label className="label" htmlFor="add-user-email">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input id="add-user-email" type="email" className="input" {...register('email')} />
                  {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
                  {!errors.email && duplicate && <p className="mt-1 text-sm text-red-500">{EMAIL_DUPLICATE}</p>}
                </div>
                <div className="field">
                  <label className="label" htmlFor="add-user-role">Role</label>
                  <select id="add-user-role" className="input" {...register('role')}>
                    {Object.entries(USER_ROLE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                {hasMlConsumer && (
                  <div className="field sm:col-span-2">
                    <label className="label" htmlFor="add-user-ml-consumer">MeridianLink Consumer Username</label>
                    <input id="add-user-ml-consumer" className="input" {...register('mlConsumerUserName')} />
                  </div>
                )}
                {hasMlMortgage && (
                  <div className="field sm:col-span-2">
                    <label className="label" htmlFor="add-user-ml-mortgage">MeridianLink Mortgage Username</label>
                    <input id="add-user-ml-mortgage" className="input" {...register('mlMortgageUserName')} />
                  </div>
                )}
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-[16px] font-medium leading-6 text-content">Product Access</h3>
              <p className="text-xs leading-[18px] text-content-secondary">{ACCESS_HINT}</p>
              <button
                type="button"
                role="switch"
                aria-checked={accessOn}
                aria-label={EDIT_ACCESS_LABEL}
                onClick={toggleAccess}
                className="flex items-center gap-4 text-sm text-content-secondary"
              >
                <img src={accessOn ? toggleOn : toggleOff} alt="" />
                {EDIT_ACCESS_LABEL}
              </button>
              {accessOn && (
                <>
                  <div className="flex items-start gap-3 rounded-lg border border-brand bg-brand-tint px-4 py-3 text-sm text-brand-deep dark:text-content">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <p>{ACCESS_ON_TEXT}</p>
                  </div>
                  <ProductAccordionTree
                    tree={tree}
                    selected={productAccess}
                    onToggle={toggleProduct}
                    visibleUrns={lenderProducts}
                  />
                </>
              )}
            </section>

            {failed && (
              <p role="alert" className="text-sm text-red-600">{ADD_FAILED}</p>
            )}
          </div>
          <div className="mt-8 flex shrink-0 items-center justify-between gap-4">
            <button
              type="button"
              onClick={onClose}
              disabled={addUser.isPending}
              className="h-11 w-[154px] rounded-lg border border-brand bg-surface text-base font-semibold text-content disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSave}
              className="h-11 w-[151px] rounded-lg bg-brand text-base font-semibold text-white disabled:opacity-20"
            >
              Add User
            </button>
          </div>
        </form>
      </section>

      <WizardDialog
        open={confirmingDiscard}
        title={DISCARD_ACCESS_TITLE}
        body={DISCARD_ACCESS_BODY}
        confirmLabel={DISCARD_ACCESS_CONFIRM}
        cancelLabel={DISCARD_ACCESS_CANCEL}
        onConfirm={() => { setConfirmingDiscard(false); setProductAccess(null); }}
        onCancel={() => setConfirmingDiscard(false)}
      />
    </div>
  );
}
